import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useNotification } from '../../hooks/useNotification';
import { useAuth } from '../../hooks/useAuth';
import SEO from '../../components/SEO/SEO';

import {
    FaUser,
    FaEnvelope,
    FaPhone,
    FaLock,
    FaEye,
    FaEyeSlash,
    FaArrowRight,
    FaCheck
} from 'react-icons/fa';
import { BsWhatsapp } from 'react-icons/bs';
import Layout from '../../components/Layout/Layout';
import Button from '../../components/Common/Button';
import Alert from '../../components/Common/Alert';
import styles from './styles/RegisterPage.module.css';

export default function RegisterPage() {
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const { register } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        password: '',
        confirmPassword: '',
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        setError('');
    };

    const validateForm = () => {
        if (formData.name.trim().length < 2) {
            setError('Le nom doit contenir au moins 2 caractères');
            return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError('Email invalide (ex: votre@email.com)');
            return false;
        }

        if (formData.phone.trim().length < 10) {
            setError('Le téléphone doit contenir au moins 10 chiffres');
            return false;
        }

        if (formData.password.length < 6) {
            setError('Le mot de passe doit contenir au moins 6 caractères');
            return false;
        }

        if (formData.password !== formData.confirmPassword) {
            setError('Les mots de passe ne correspondent pas');
            return false;
        }

        return true;
    };

    const getErrorMessage = (errorCode) => {
        const errorMessages = {
            'auth/email-already-in-use': 'Cet email est déjà utilisé. Veuillez vous connecter ou utiliser un autre email.',
            'auth/invalid-email': 'Email invalide.',
            'auth/weak-password': 'Le mot de passe est trop faible. Utilisez au moins 6 caractères.',
            'auth/operation-not-allowed': 'L\'inscription est actuellement désactivée.',
            'auth/too-many-requests': 'Trop de tentatives. Réessayez plus tard.',
        };

        return errorMessages[errorCode] || `Erreur: ${errorCode}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            await register({
                name: formData.name.trim(),
                email: formData.email.trim(),
                phone: formData.phone.trim(),
                password: formData.password,
            });

            showNotification('Compte créé avec succès!', 'success');

            setTimeout(() => {
                navigate('/reservation', { replace: true });
            }, 1500);
        } catch (error) {
            const errorMessage = getErrorMessage(error.code);
            setError(errorMessage);
            showNotification(errorMessage, 'error');
            setLoading(false);
        }
    };

    return (
        <>
            <SEO
                title="Créer un Compte | OV Hairstyle"
                description="Créez votre compte OV Hairstyle et accédez à nos services premium de coiffure à domicile."
                url="https://ovhairstyle.com/register"
                image="https://ov-hairstyle.vercel.app/images/og-image.jpeg"
            />
            <Layout>
                <div className={styles.registerContainer}>
                    {/* Éléments de décoration */}
                    <div className={styles.decoration1}></div>
                    <div className={styles.decoration2}></div>

                    <div className={styles.content}>
                        {/* Colonne gauche - Texte */}
                        <div className={styles.leftColumn}>
                            <div className={styles.leftContent}>
                                <h1 className={styles.mainTitle}>
                                    Rejoignez<br />
                                    <span className={styles.highlight}>OV Hairstyle</span>
                                </h1>
                                <p className={styles.description}>
                                    Créez votre compte et accédez à nos services premium de coiffure à domicile
                                </p>

                                <div className={styles.benefits}>
                                    <div className={styles.benefit}>
                                        <div className={styles.benefitIcon}>
                                            <FaCheck />
                                        </div>
                                        <div>
                                            <h4>Service Premium</h4>
                                            <p>Accès à tous nos services haut de gamme</p>
                                        </div>
                                    </div>
                                    <div className={styles.benefit}>
                                        <div className={styles.benefitIcon}>
                                            <FaCheck />
                                        </div>
                                        <div>
                                            <h4>Réservations Faciles</h4>
                                            <p>Planifiez vos rendez-vous en quelques clics</p>
                                        </div>
                                    </div>
                                    <div className={styles.benefit}>
                                        <div className={styles.benefitIcon}>
                                            <FaCheck />
                                        </div>
                                        <div>
                                            <h4>Support 24/7</h4>
                                            <p>Assistance disponible via WhatsApp</p>
                                        </div>
                                    </div>
                                </div>

                                <div className={styles.testimonialBox}>
                                    <p className={styles.testimonialText}>
                                        "Service impeccable! Je gagne du temps et les résultats sont excellents."
                                    </p>
                                    <p className={styles.testimonialAuthor}>— Mishiku Gertrude</p>
                                </div>
                            </div>
                        </div>

                        {/* Colonne droite - Formulaire */}
                        <div className={styles.rightColumn}>
                            <div className={styles.formCard}>
                                <div className={styles.formHeader}>
                                    <h2>Créer un compte</h2>
                                    <p>Rejoignez notre communauté</p>
                                </div>

                                {error && (
                                    <Alert
                                        type="error"
                                        message={error}
                                        onClose={() => setError('')}
                                    />
                                )}

                                <form onSubmit={handleSubmit} className={styles.form}>
                                    {/* Nom */}
                                    <div className={styles.formGroup}>
                                        <label htmlFor="name">Nom complet</label>
                                        <div className={styles.inputWrapper}>
                                            <FaUser className={styles.inputIcon} />
                                            <input
                                                id="name"
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder="Jean Dupont"
                                                required
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>

                                    {/* Email */}
                                    <div className={styles.formGroup}>
                                        <label htmlFor="email">Email</label>
                                        <div className={styles.inputWrapper}>
                                            <FaEnvelope className={styles.inputIcon} />
                                            <input
                                                id="email"
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder="votre@email.com"
                                                required
                                                autoComplete="email"
                                                disabled={loading}
                                            />
                                        </div>
                                    </div>

                                    {/* Téléphone */}
                                    <div className={styles.formGroup}>
                                        <label htmlFor="phone">Téléphone</label>
                                        <div className={styles.inputWrapper}>
                                            <FaPhone className={styles.inputIcon} />
                                            <input
                                                id="phone"
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                placeholder="+243 97 23 15 704"
                                                required
                                                disabled={loading}
                                            />
                                        </div>
                                        <small className={styles.hint}>
                                            Au moins 10 chiffres
                                        </small>
                                    </div>

                                    {/* Mot de passe */}
                                    <div className={styles.formGroup}>
                                        <label htmlFor="password">Mot de passe</label>
                                        <div className={styles.inputWrapper}>
                                            <FaLock className={styles.inputIcon} />
                                            <input
                                                id="password"
                                                type={showPassword ? 'text' : 'password'}
                                                name="password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                placeholder="••••••••"
                                                required
                                                autoComplete="new-password"
                                                disabled={loading}
                                            />
                                            <button
                                                type="button"
                                                className={styles.togglePassword}
                                                onClick={() => setShowPassword(!showPassword)}
                                                disabled={loading}
                                            >
                                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                        <small className={styles.hint}>
                                            Minimum 6 caractères
                                        </small>
                                    </div>

                                    {/* Confirmer mot de passe */}
                                    <div className={styles.formGroup}>
                                        <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
                                        <div className={styles.inputWrapper}>
                                            <FaLock className={styles.inputIcon} />
                                            <input
                                                id="confirmPassword"
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                name="confirmPassword"
                                                value={formData.confirmPassword}
                                                onChange={handleChange}
                                                placeholder="••••••••"
                                                required
                                                autoComplete="new-password"
                                                disabled={loading}
                                            />
                                            <button
                                                type="button"
                                                className={styles.togglePassword}
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                disabled={loading}
                                            >
                                                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Bouton Inscription */}
                                    <Button
                                        type="submit"
                                        size="large"
                                        loading={loading}
                                        className={styles.submitBtn}
                                        disabled={loading}
                                    >
                                        {loading ? 'Création en cours...' : 'Créer un compte'}
                                        {!loading && <FaArrowRight className={styles.btnIcon} />}
                                    </Button>
                                </form>

                                {/* Lien connexion */}
                                <div className={styles.footer}>
                                    <p>
                                        Déjà inscrit?{' '}
                                        <Link to="/login" className={styles.link}>
                                            Se connecter
                                        </Link>
                                    </p>
                                </div>

                                {/* Divider */}
                                <div className={styles.divider}>
                                    <span>ou</span>
                                </div>

                                {/* WhatsApp CTA */}
                                <a href="https://wa.me/243972315704" target="_blank" rel="noreferrer" className={styles.whatsappQuickLink}>
                                    <BsWhatsapp /> Nous contacter
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        </>
    );
}
